<template>
  <DataBox v-if="!hasError">
    <template v-slot:databoxContent>
      <DataSection>
        <template v-slot:dataLines>
          <!-- Partner Name Fields -->
          <template v-if="partnerName">
            <DataLine
              v-if="shouldDisplaySalutation(DataLineLabelKeys.Salutation, partnerName)"
              :label="t('partnerAddress.salutation')"
              :value="partnerName.salutation?.value"
            ></DataLine>
            <DataLine
              v-if="
                shouldDisplayAcademicTitle(DataLineLabelKeys.AcademicTitle, partnerName)
              "
              :label="t('partnerAddress.academicTitle')"
              :value="(partnerName as NaturalPersonName).academicTitle"
            ></DataLine>
            <DataLine
              v-if="
                shouldDisplayNobilityTitle(DataLineLabelKeys.NobilityTitle, partnerName)
              "
              :label="t('partnerAddress.nobilityTitle')"
              :value="(partnerName as NaturalPersonName).nobilityTitle"
            ></DataLine>
            <DataLine
              v-if="shouldDisplayFirstName(DataLineLabelKeys.FirstName, partnerName)"
              :label="t('partnerAddress.firstName')"
              :value="(partnerName as NaturalPersonName).firstName"
            ></DataLine>
            <DataLine
              v-if="shouldDisplayName(DataLineLabelKeys.LastName, partnerName)"
              :iconSpan="showSelfService ? nameIconData : undefined"
              :label="t('partnerAddress.lastName')"
              :value="(partnerName as NaturalPersonName).name"
              @click:icon="navigateToSelfService"
            >
            </DataLine>
            <DataLine
              v-if="
                shouldDisplayLine(
                  DataLineLabelKeys.NameAffix,
                  (partnerName as NaturalPersonName).suffix
                )
              "
              :label="t('partnerAddress.nameAffix')"
              :value="(partnerName as NaturalPersonName).suffix"
            ></DataLine>
            <DataLine
              :label="t('partnerAddress.dateOfBirth')"
              :value="
                partnerPerson && (partnerPerson as NaturalPerson).birthdate
                  ? formatIsoDateStringLocal(
                      (partnerPerson as NaturalPerson).birthdate || ''
                    )
                  : '-'
              "
            ></DataLine>
          </template>
          <!-- Partner Address Fields -->
          <template v-if="address">
            <DataLine
              v-if="
                shouldDisplayLine(
                  DataLineLabelKeys.StreetAndHouseNumber,
                  (address as PhysicalAddress).street
                )
              "
              :iconSpan="showSelfService ? addressIconData : undefined"
              :label="t('partnerAddress.streetAndHouseNumber')"
              :value="(address as PhysicalAddress).street"
              @click:icon="navigateToSelfService"
            >
            </DataLine>
            <DataLine
              v-if="
                shouldDisplayLine(
                  DataLineLabelKeys.AdditionalToAddress,
                  (address as PhysicalAddress).streetExtra
                )
              "
              :label="t('partnerAddress.additionalToAddress')"
              :value="(address as PhysicalAddress).streetExtra"
            ></DataLine>
            <DataLine
              v-if="
                address.postCode &&
                isDataLineWhitelisted(DataLineLabelKeys.ZipcodeAndCity)
              "
              :iconSpan="showSelfService ? addressIconData : undefined"
              :label="t('partnerAddress.zipcode')"
              :value="address.postCode"
              @click:icon="navigateToSelfService"
            >
            </DataLine>
            <DataLine
              v-if="
                (address as PhysicalAddress).city &&
                isDataLineWhitelisted(DataLineLabelKeys.ZipcodeAndCity)
              "
              :iconSpan="showSelfService ? addressIconData : undefined"
              :label="t('partnerAddress.city')"
              :value="(address as PhysicalAddress).city"
              @click:icon="navigateToSelfService"
            >
            </DataLine>
            <DataLine
              v-if="
                address.country &&
                shouldDisplayLine(DataLineLabelKeys.Country, address.country.value)
              "
              :label="t('partnerAddress.country')"
              :value="address.country.value"
            ></DataLine>
            <DataLine
              v-for="(emailContact, index) in getDisplayedEmails"
              :key="index"
              :label="t('partnerAddress.email') + ` ${index + 1}`"
              :value="emailContact.email"
            ></DataLine>
          </template>
        </template>
      </DataSection>
    </template>
  </DataBox>
  <p v-else class="p">{{ t('partnerAddress.errorText') }}</p>
</template>

<script lang="ts" setup>
import type { PropType } from 'vue'
import { computed } from 'vue'
import { DataBox, DataLine, DataSection } from 'de-zurich-ui-library'
import type {
  EmailContact,
  LegalPersonName,
  NaturalPerson,
  NaturalPersonName,
  PartnerAddress,
  PhysicalAddress
} from 'partner-api'
import { DataLineLabelKeys } from '@/types/partner-proxy/PartnerProxyTypes'
import { navigate } from '@/utils/NavigationUtils'
import { formatIsoDateString } from '@/utils/utils'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

type PersonNameUnion = NaturalPersonName | LegalPersonName

const { hasError, partnerAddress, showSelfServiceIcon, dataLineWhitelist } = defineProps({
  hasError: {
    type: Boolean,
    required: true
  },
  partnerAddress: {
    type: [Object, null] as PropType<PartnerAddress | null>,
    required: true
  },
  showSelfServiceIcon: {
    type: String,
    default: 'true'
  },
  dataLineWhitelist: {
    type: Array<string>,
    validator(dataLineWhitelist: string[]): boolean {
      const supportedDataLineKeys: string[] = Object.values(DataLineLabelKeys)
      return (
        !dataLineWhitelist ||
        dataLineWhitelist.every((dataLineKey) => {
          if (!supportedDataLineKeys.includes(dataLineKey)) {
            console.error(
              'Unsupported provided data line key: ' +
                dataLineKey +
                '.\nSupported data lines keys are: ' +
                supportedDataLineKeys
            )
            return false
          }

          return true
        })
      )
    }
  }
})

const changeNameURL: string =
  globalThis.ENV_CONFIG?.SERVICE_LINKS.PERSONAL_PAGE.CHANGE_NAME_PAGE
const changeAddressURL: string =
  globalThis.ENV_CONFIG?.SERVICE_LINKS.PERSONAL_PAGE.CHANGE_ADDRESS_PAGE

const partnerPerson = computed(() =>
  partnerAddress?.partner ? partnerAddress.partner.person : null
)

const partnerName = computed((): PersonNameUnion | null =>
  partnerPerson.value ? ((partnerPerson.value as NaturalPerson).name ?? null) : null
)

const address = computed(() => (partnerAddress ? partnerAddress.address : null))

const contacts = computed(() => (partnerAddress ? partnerAddress.contacts : null))

const emails = computed(() => {
  const emailContacts: EmailContact[] = []
  if (!contacts.value) {
    return emailContacts
  }
  contacts.value.forEach((contact) => {
    if (contact.type === 'EmailContact' && 'email' in contact) {
      emailContacts.push(contact as EmailContact)
    }
  })
  return emailContacts
})

const getDisplayedEmails = computed((): EmailContact[] => {
  return emails.value.filter((emailContact) =>
    shouldDisplayLine(DataLineLabelKeys.Emails, emailContact.email ?? '')
  )
})

const formatIsoDateStringLocal = (isoDate: string) => {
  return formatIsoDateString(isoDate)
}

const isDataLineWhitelisted = (dataLineLabel: string): boolean => {
  return (
    !dataLineWhitelist ||
    dataLineWhitelist.length === 0 ||
    dataLineWhitelist.includes(dataLineLabel)
  )
}

const shouldDisplayLine = (dataLineLabel: string, dataLineValue?: string) => {
  return (
    dataLineValue !== undefined &&
    dataLineValue !== null &&
    dataLineValue !== '' &&
    isDataLineWhitelisted(dataLineLabel)
  )
}

const shouldDisplaySalutation = (dataLineLabel: string, person: PersonNameUnion) => {
  return (
    person?.salutation?.value !== undefined &&
    person?.salutation?.value !== null &&
    person?.salutation?.value !== '' &&
    isDataLineWhitelisted(dataLineLabel)
  )
}

const shouldDisplayAcademicTitle = (dataLineLabel: string, person: PersonNameUnion) => {
  return shouldDisplayLine(
    dataLineLabel,
    'academicTitle' in person ? person.academicTitle : undefined
  )
}

const shouldDisplayNobilityTitle = (dataLineLabel: string, person: PersonNameUnion) => {
  return shouldDisplayLine(
    dataLineLabel,
    'nobilityTitle' in person ? person.nobilityTitle : undefined
  )
}

const shouldDisplayFirstName = (dataLineLabel: string, person: PersonNameUnion) => {
  return shouldDisplayLine(
    dataLineLabel,
    'firstName' in person ? person.firstName : undefined
  )
}

const shouldDisplayName = (dataLineLabel: string, person: PersonNameUnion) => {
  return shouldDisplayLine(dataLineLabel, 'name' in person ? person.name : undefined)
}

const navigateToSelfService = (href: string, isRouterLink: boolean) => {
  // DataBox has result href as a string, DataLine has result object with href and isRouterLink
  navigate(href)
}

const nameIconData = {
  iconClass: 'icon-name',
  href: changeNameURL,
  label: 'Name ändern'
}

const showSelfService = showSelfServiceIcon === 'true'

const addressIconData = {
  iconClass: 'icon-adresse',
  href: changeAddressURL,
  label: 'Adresse ändern'
}
</script>

<style lang="scss" scoped>
.p {
  letter-spacing: 0.2px;
  line-height: 24px;
  display: inline-block;
  height: 72px;
  font-size: 16px;
  font-family: var(--f-primary-light);
  color: #111111;
}

/* Workaround for bug in ui-lib, too much padding around section */

.section {
  padding: 0;
}
</style>
